import React, { lazy, Suspense, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import BaseInput from 'flow/components/ui-components/BaseInput';
import BaseModal from 'flow/components/ui-components/BaseModal';
import BaseTextArea from 'flow/components/ui-components/BaseTextArea';
import Icon from 'flow/components/ui-components/Icon';
import { useModal } from 'flow/contexts/ModalProvider';
import {
    useCreatePremadeAnswer,
    useUpdatePremadeAnswer,
} from 'flow/hooks/api/usePremadeAnswer';
import useAppContext from 'flow/hooks/useAppContext';
import useAutoFocus from 'flow/hooks/useAutoFocus';
import useDarkMode from 'flow/hooks/useDarkMode';
import usePremadeAnswers, { placeholders } from 'flow/hooks/usePremadeAnswers';
import { PremadeAnswer, PremadeAnswerType } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';

interface Props {
    chatSessionKey?: ChatSessionKey;
    premadeAnswer?: PremadeAnswer;
    type?: PremadeAnswerType;
    onClose?: () => void;
}

// Roughly 125 KB gzipped of editor. Loading it lazily keeps it out of the main
// bundle for the majority of sessions that never open a signature.
const SignatureEditor = lazy(
    () =>
        import('flow/components/ui-components/SignatureEditor/SignatureEditor'),
);

const escapeHtml = (str: string) =>
    str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const syntaxColors = {
    dark: {
        tag: '#569cd6',
        attr: '#9cdcfe',
        value: '#ce9178',
        bracket: '#808080',
        text: '#d4d4d4',
    },
    light: {
        tag: '#0550ae',
        attr: '#116329',
        value: '#a31515',
        bracket: '#6a737d',
        text: '#24292f',
    },
};

const highlightHtmlSyntax = (source: string, isDark: boolean): string => {
    const c = isDark ? syntaxColors.dark : syntaxColors.light;
    // Both patterns are linear (single negated-class runs, no overlapping
    // quantifiers) so an unclosed "<tag ..." in a shared signature can't trigger
    // catastrophic backtracking. `[^&]*` captures the whole tag interior in one
    // pass (it stops at the `&` that begins the `&gt;` terminator); the trailing
    // self-closing slash is peeled off in JS rather than in the regex.
    return escapeHtml(source).replace(
        /&lt;(\/?)([a-zA-Z][a-zA-Z0-9]*)([^&]*)&gt;/g,
        (_match, slash, tag, rest) => {
            const selfClose = /\/\s*$/.test(rest) ? '/' : '';
            const attrs = selfClose ? rest.replace(/\/\s*$/, '') : rest;
            // Escaped attrs contain no `&`, so the only quote char is a literal
            // `"`/`'`; `[^"']*` can't span the closing quote → no backtracking.
            const coloredAttrs = attrs.replace(
                /([a-zA-Z-]+)(=)(["'])([^"']*)\3/g,
                `<span style="color:${c.attr}">$1</span><span style="color:${c.text}">$2</span><span style="color:${c.value}">$3$4$3</span>`,
            );
            return `<span style="color:${c.bracket}">&lt;${slash}</span><span style="color:${c.tag}">${tag}</span>${coloredAttrs}<span style="color:${c.bracket}">${selfClose}&gt;</span>`;
        },
    );
};

const PremadeAnswerModal = ({
    chatSessionKey,
    premadeAnswer,
    type,
    onClose,
}: Props) => {
    const { i18next } = useAppContext();
    const isDark = useDarkMode();
    const nameRef = useAutoFocus();
    const { insertPlaceholder, substitutePlaceholders } =
        usePremadeAnswers(chatSessionKey);
    const createPremadeAnswer = useCreatePremadeAnswer();
    const updatePremadeAnswer = useUpdatePremadeAnswer();

    const messageRef = useRef<HTMLTextAreaElement>(null);
    const backdropRef = useRef<HTMLDivElement>(null);
    const [name, setName] = useState(premadeAnswer?.name ?? '');
    const [message, setMessage] = useState(premadeAnswer?.message ?? '');
    const [isDefault, setIsDefault] = useState(
        premadeAnswer?.isDefault ?? false,
    );
    const isEmailSignature =
        (type ?? premadeAnswer?.type) === 'EMAIL_SIGNATURE';
    // Defaults are only used by the email signature flow; hide the toggle for
    // plain premade answers so they can't clear an existing default signature.
    const showDefaultToggle = isEmailSignature;
    // The rich text editor is the signature UI; the HTML view behind it is an
    // escape hatch for support and internal testing, not a customer feature.
    const [showSource, setShowSource] = useState(false);

    const { previousModal } = useModal();

    const highlighted = useMemo(
        () =>
            isEmailSignature ? highlightHtmlSyntax(message, isDark) + '\n' : '',
        [isEmailSignature, message, isDark],
    );

    // Nothing substitutes placeholders on the email path, so resolving them
    // here would preview something the recipient never sees. Chat answers do
    // substitute. Hoisted out of the JSX: inside it this sat within the
    // `message ? ... : null` branch, making two conditionals in one expression.
    const previewHtml = isEmailSignature
        ? message
        : substitutePlaceholders(message);

    const sourceToggleLabel = showSource
        ? i18next.t('signatureEditor.showEditor')
        : i18next.t('signatureEditor.showHtml');

    const syncScroll = () => {
        if (messageRef.current && backdropRef.current) {
            backdropRef.current.scrollTop = messageRef.current.scrollTop;
            backdropRef.current.scrollLeft = messageRef.current.scrollLeft;
        }
    };

    return (
        <BaseModal
            open={true}
            header={i18next.t(
                isEmailSignature
                    ? 'signatureEditor.dialogTitle'
                    : 'premadeAnswer.dialogTitle',
            )}
            disableSubmit={name.trim() === '' || message.trim() === ''}
            // Pop just this editor off the modal stack (returning to the
            // signature picker if it opened us) rather than clearing the whole
            // stack; the submit handler pops explicitly, cancel pops via
            // BaseModal's onClose→previousModal.
            preventCloseOnSubmit={true}
            onSubmit={() => {
                if (premadeAnswer) {
                    updatePremadeAnswer.mutate({
                        key: premadeAnswer.key,
                        name,
                        message,
                        isDefault,
                        type: type ?? premadeAnswer.type,
                    });
                } else {
                    createPremadeAnswer.mutate({
                        name,
                        message,
                        isDefault,
                        type,
                    });
                }
                onClose?.();
                previousModal();
            }}
            onClose={onClose}
            className="w-[600px] overflow-visible"
        >
            <div className="flex flex-col gap-3">
                <BaseInput
                    ref={nameRef as React.MutableRefObject<HTMLInputElement>}
                    label={i18next.t('premadeAnswer.name')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />

                {isEmailSignature ? (
                    <div className="flex w-full flex-col gap-y-1.5">
                        <div className="flex items-center justify-between">
                            <label className="flex text-sm font-bold text-gray-800">
                                {i18next.t('premadeAnswer.template')}
                            </label>
                            <button
                                type="button"
                                onClick={() => setShowSource((shown) => !shown)}
                                className="rounded border px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-100"
                            >
                                {sourceToggleLabel}
                            </button>
                        </div>
                        {!showSource && (
                            <Suspense
                                fallback={
                                    <div className="min-h-40 w-full animate-pulse rounded-md bg-gray-100" />
                                }
                            >
                                <SignatureEditor
                                    value={message}
                                    onChange={setMessage}
                                />
                            </Suspense>
                        )}
                        {showSource && (
                            <div className="relative min-h-40 w-full rounded-md bg-gray-100 dark:bg-[#1e1e1e]">
                                <div
                                    ref={backdropRef}
                                    aria-hidden={true}
                                    className="pointer-events-none absolute inset-0 overflow-hidden p-3 text-sm/4 break-words whitespace-pre-wrap text-gray-800 dark:text-[#d4d4d4]"
                                    dangerouslySetInnerHTML={{
                                        __html: highlighted,
                                    }}
                                />
                                <textarea
                                    ref={messageRef}
                                    aria-label={i18next.t(
                                        'premadeAnswer.message',
                                    )}
                                    className="relative z-10 block min-h-40 w-full resize-y bg-transparent p-3 text-sm/4 text-transparent caret-gray-800 focus:outline-none dark:caret-white"
                                    spellCheck={false}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onScroll={syncScroll}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <BaseTextArea
                        ref={messageRef}
                        label={i18next.t('premadeAnswer.template')}
                        className="min-h-40 resize-y"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                    />
                )}

                <div className="flex items-center justify-between">
                    {showDefaultToggle ? (
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={isDefault}
                                onChange={(e) => setIsDefault(e.target.checked)}
                            />
                            {i18next.t('premadeAnswer.markAsDefault')}
                        </label>
                    ) : (
                        <span />
                    )}

                    {/*
                        Placeholders resolve against the latest incoming *chat*
                        call and are only substituted on the chat send path
                        (see substitutePlaceholders usages). A signature goes
                        out through buildEmailBody, which never substitutes, so
                        offering them here mails a literal "[name]" to the
                        customer. Chat premade answers keep the menu.
                    */}
                    {isEmailSignature ? null : (
                        <Menu>
                            <MenuButton className="flex items-center gap-x-2 rounded border px-2 py-1 text-sm hover:bg-gray-100">
                                <p>{i18next.t('premade_answers.variables')}</p>

                                <Icon iconKey="arrow_down" size="1rem" />
                            </MenuButton>

                            <MenuItems className="absolute end-4 z-10 w-[320px] rounded bg-white p-2 shadow-lg">
                                {placeholders.map((variable) => (
                                    <MenuItem key={variable.key}>
                                        <button
                                            className="flex w-full justify-between p-2 text-start hover:bg-gray-100"
                                            onClick={() => {
                                                insertPlaceholder(
                                                    messageRef?.current,
                                                    variable.key,
                                                );
                                                setMessage(
                                                    messageRef?.current
                                                        ?.value || '',
                                                );
                                            }}
                                        >
                                            <p>{i18next.t(variable.label)}</p>
                                            <p className="text-gray-400 group-hover:text-gray-800">
                                                {variable.key}
                                            </p>
                                        </button>
                                    </MenuItem>
                                ))}
                            </MenuItems>
                        </Menu>
                    )}
                </div>

                {message ? (
                    <div className="flex flex-col gap-y-1.5 text-sm">
                        <p className="text-sm font-bold text-gray-800">
                            {i18next.t('preview')}
                        </p>

                        <div
                            className="bg-yellow/10 rounded p-3"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(previewHtml),
                            }}
                        />
                    </div>
                ) : null}
            </div>
        </BaseModal>
    );
};

export default PremadeAnswerModal;
